import { formatTemp } from './units.js';
import { formatSalinity, readSalinityUnit, salinityUnitLabel } from './salinity.js';

// A resident's journey, in words: its status, and how long it was kept.
// Pure, so the wording is testable. Durations are rounded and said as
// "about": the added date is often a guess ("sometime in March"), and a
// day count would claim a precision nobody has (docs/ANTI-PATTERNS.md #13).

export const STATUS_LABEL = {
  in_tank: 'In the tank',
  died: 'Died',
  lost: 'Lost',
  rehomed: 'Rehomed',
  other: 'Something else',
  tank_closed: 'Tank taken down',
};

// The choices offered when something happens to a resident.
export const END_CHOICES = [
  { k: 'died', label: 'Died' },
  { k: 'lost', label: 'Lost', hint: 'Missing, jumped, or never found' },
  { k: 'rehomed', label: 'Rehomed', hint: 'Sold, traded, given away or moved' },
  { k: 'other', label: 'Something else' },
];

const DAY = 86_400_000;

function toDay(v) {
  if (!v) return null;
  const t = new Date(String(v).length === 10 ? `${v}T12:00:00Z` : v).getTime();
  return Number.isFinite(t) ? t : null;
}

// "about 1 year 3 months" / "about 5 weeks" / "less than a week". Null
// when the added date is unknown: better to say nothing than guess.
export function keptForLabel(addedAt, endedAt = null, now = Date.now()) {
  const from = toDay(addedAt);
  const to = endedAt ? toDay(endedAt) : now;
  if (from == null || to == null || to < from) return null;
  const days = Math.round((to - from) / DAY);
  if (days < 7) return 'less than a week';
  if (days < 60) {
    const w = Math.round(days / 7);
    return `about ${w} week${w === 1 ? '' : 's'}`;
  }
  const months = Math.round(days / 30.44);
  if (months < 12) return `about ${months} months`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  const years = `${y} year${y === 1 ? '' : 's'}`;
  return m ? `about ${years} ${m} month${m === 1 ? '' : 's'}` : `about ${years}`;
}

// "Green Slimer" / "Green Slimer (big colony, left rock)".
export function residentName(r) {
  const name = r?.common_name || 'Unnamed';
  return r?.nickname ? `${name} (${r.nickname})` : name;
}

export function isEnded(r) {
  return !!r && r.status && r.status !== 'in_tank';
}

// Units for the canonical storage values the readings snapshot holds.
const CANONICAL_UNIT = {
  alk: 'dKH',
  calcium: 'ppm',
  magnesium: 'ppm',
  nitrate: 'ppm',
  phosphate: 'ppm',
  salinity: 'ppt',
  temp: '°F',
  ammonia: 'ppm',
  nitrite: 'ppm',
};

// A snapshot value in the keeper's own units: temperature per the tank's
// setting, salinity per this device's preference, like every other
// screen. Storage stays canonical (°F, ppt).
export function snapshotValue(param, value, displayUnits) {
  if (param === 'temp') return formatTemp(value, displayUnits?.temp === 'C' ? 'C' : 'F');
  if (param === 'salinity') {
    const unit = readSalinityUnit();
    return `${formatSalinity(value, unit)} ${salinityUnitLabel(unit)}`;
  }
  return `${value}${CANONICAL_UNIT[param] ? ` ${CANONICAL_UNIT[param]}` : ''}`;
}
